import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faEdit,
  faEye,
  faTimes,
  faBuilding,
  faHome,
  faMoneyBillWave,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Oval } from "react-loader-spinner"; // Assuming you have this installed
import { StandardURL } from "../App";

// Set app element for react-modal to avoid accessibility warnings
Modal.setAppElement("#root"); // Make sure your root element has id="root" in your index.html

const Rentals = () => {
  const [rentals, setRentals] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true); // Added loading state for initial fetch
  const [addingRental, setAddingRental] = useState(false); // For modal submission loading
  const [fetchError, setFetchError] = useState(null); // Added state for fetch errors
  const navigate = useNavigate();

  // State for the new rental form inputs
  const [newRentalRentPrice, setNewRentalRentPrice] = useState("");
  const [newRentalHouseType, setNewRentalHouseType] = useState("");
  const [newRentalDescription, setNewRentalDescription] = useState("");
  const [newRentalHouseNumber, setNewRentalHouseNumber] = useState("");

  useEffect(() => {
    const fetchRentalsData = async () => {
      setLoading(true);
      setFetchError(null); // Clear previous errors

      const propertyId = localStorage.getItem("selectedPropertyId");
      const token = localStorage.getItem("token");

      if (!propertyId || !token) {
        setFetchError("Property not selected or user not authenticated.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${StandardURL}/iNyumba/admin/rentals/propertyall/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setRentals(Array.isArray(response.data.response) ? response.data.response : []);
        if (Array.isArray(response.data.response) && response.data.response.length === 0) {
          toast.info("No rental units found for this property.");
        } else {
          toast.success("Rental units fetched successfully!");
        }
      } catch (error) {
        console.error("Error fetching rentals:", error);
        if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
          setFetchError("No rental units found for this property.");
          toast.info("No rental units found for this property.");
        } else {
          setFetchError(error.response?.data?.response || "Failed to fetch rental units.");
          toast.error(error.response?.data?.response || "Failed to fetch rental units.");
        }
        setRentals([]); // Clear existing data on error
      } finally {
        setLoading(false);
      }
    };

    fetchRentalsData();
  }, []); // Empty dependency array means this runs once on mount

  const handleAddRental = () => {
    // Reset form fields when opening the add modal
    setNewRentalRentPrice("");
    setNewRentalHouseType("");
    setNewRentalDescription("");
    setNewRentalHouseNumber("");
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
  };

  const addRental = async (e) => { // Made async to use await
    e.preventDefault(); // Prevent default form submission
    setAddingRental(true); // Start loading for modal submission

    const propertyId = localStorage.getItem("selectedPropertyId");
    const token = localStorage.getItem("token");

    if (
      !propertyId ||
      !token ||
      !newRentalRentPrice ||
      !newRentalHouseType ||
      !newRentalDescription ||
      !newRentalHouseNumber
    ) {
      toast.error("All fields are required.");
      setAddingRental(false);
      return;
    }

    const newRentalData = {
      rent_price: newRentalRentPrice,
      house_type: newRentalHouseType,
      description: newRentalDescription,
      house_number: newRentalHouseNumber,
    };

    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/admin/rentals/property/${propertyId}`,
        newRentalData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.response);
      closeModal();

      const updatedResponse = await axios.get( // Re-fetch all rentals after successful addition
        `${StandardURL}/iNyumba/admin/rentals/propertyall/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setRentals(Array.isArray(updatedResponse.data.response) ? updatedResponse.data.response : []);
    } catch (error) {
      toast.error(error.response?.data?.response || "Error adding rental.");
      console.error("Error adding rental:", error);
    } finally {
      setAddingRental(false); // Stop loading for modal submission
    }
  };

  const handleUpdateRental = (rentalId) => {
    localStorage.setItem("selectedRentalId", rentalId);
    navigate("/landlord/updaterental");
  };

  const handleViewRental = (rentalId) => {
    localStorage.setItem("selectedRentalId", rentalId);
    navigate("/landlord/specificrental");
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            {/* Header and Add Button */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
              <button
                onClick={() => navigate("/landlord/home")}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200 mb-4 sm:mb-0"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 text-center flex-grow">
                <FontAwesomeIcon icon={faHome} className="mr-3 text-indigo-600" />
                Rental Units
              </h1>
              <button
                onClick={handleAddRental}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Add New Rental
              </button>
            </div>

            {/* Loading state */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#4F46E5" // Indigo-600
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel='loading-indicator'
                  secondaryColor="#C7D2FE" // Indigo-200
                  strokeWidth={4}
                  strokeWidthSecondary={4}
                />
                <p className="ml-3 text-lg text-gray-700 mt-3">Loading rental units...</p>
              </div>
            )}

            {/* Error state */}
            {fetchError && !loading && (
              <div className="text-center py-10 bg-red-50 rounded-lg border border-red-300 text-red-700">
                <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-red-400 mb-4" />
                <p className="text-lg font-semibold mb-2">Error!</p>
                <p className="text-md">{fetchError}</p>
                <button
                  onClick={() => window.location.reload()} // Simple refresh to try again
                  className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Rental List Table */}
            {!loading && !fetchError && rentals.length > 0 ? (
              <div className="overflow-x-auto mt-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                  Available Rental Units ({rentals.length})
                </h2>
                <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md">
                  <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        House Number
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        House Type
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Monthly Rent
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Description
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Occupancy Status
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rentals.map((rental, index) => (
                      <tr
                        key={rental.id || index} // Assuming 'id' is a unique key, otherwise use index
                        className={index % 2 === 0 ? "bg-gray-50 hover:bg-gray-100" : "bg-white hover:bg-gray-100"}
                      >
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          <FontAwesomeIcon icon={faBuilding} className="mr-2 text-gray-500" />
                          {rental.house_number}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          {rental.house_type}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-600" />
                          KES {parseFloat(rental.rent_price).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-gray-600 max-w-xs overflow-hidden text-ellipsis">
                          {rental.description}
                        </td>
                        <td className="py-3 px-4 text-gray-600 max-w-xs overflow-hidden text-ellipsis">
                          {rental.status}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleUpdateRental(rental.rental_id)} // Using rental_id as per original code
                            className="text-blue-600 hover:text-blue-800 mr-3 transition-colors duration-200"
                            title="Edit Rental"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit
                          </button>
                          <button
                            onClick={() => handleViewRental(rental.rental_id)} // Using rental_id as per original code
                            className="text-purple-600 hover:text-purple-800 transition-colors duration-200"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !loading && !fetchError && rentals.length === 0 && (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">No rental units found for this property.</p>
                  <p className="text-md text-gray-500 mt-2">Click "Add New Rental" to add a new unit.</p>
                </div>
              )
            )}
          </div>

          {/* Add Rental Modal */}
          <Modal
            isOpen={showAddModal}
            onRequestClose={closeModal}
            contentLabel="Add New Rental"
            className="modal-content fixed inset-0 flex items-center justify-center p-4"
            overlayClassName="modal-overlay fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center"
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
                Add New Rental Unit
              </h2>
              <form onSubmit={addRental} className="space-y-4">
                <div>
                  <label htmlFor="houseNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    House Number
                  </label>
                  <input
                    type="text"
                    id="houseNumber"
                    value={newRentalHouseNumber}
                    onChange={(e) => setNewRentalHouseNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="houseType" className="block text-sm font-medium text-gray-700 mb-1">
                    House Type
                  </label>
                  <input
                    type="text"
                    id="houseType"
                    value={newRentalHouseType}
                    onChange={(e) => setNewRentalHouseType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="rentPrice" className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Rent (KES)
                  </label>
                  <input
                    type="number"
                    id="rentPrice"
                    value={newRentalRentPrice}
                    onChange={(e) => setNewRentalRentPrice(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={newRentalDescription}
                    onChange={(e) => setNewRentalDescription(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-5 py-2.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={addingRental}
                  >
                    {addingRental ? (
                      <Oval height={20} width={20} color="#FFF" secondaryColor="#C7D2FE" strokeWidth={5} strokeWidthSecondary={5} />
                    ) : (
                      <><FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Rental</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </Modal>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default Rentals;